import { AlertBanner, AlertBannerStatus, StyledFlex, TextField } from '@tryhackme/thm-ui-components';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AUTHENTICATION_CODE_REQUIRED_MESSAGE, LOGIN_REFERRER } from 'src/common/constants';
import { useForm } from 'src/common/form';
import { useFeatureIsOn } from 'src/common/growthbook';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import { apiSlice } from 'src/features/api/api-slice';
import { getLoginReferrer } from 'src/features/login/login.constants';
import { StyledFormSubtitle, StyledFormTitle } from 'src/features/login/login.styles';
import { useTwoFactorMutation, useVerifyRecoveryCodeMutation } from './two-factor-form.slice';
import { StyledControllerWrapper, StyledForm, StyledFullWidthButton, StyledTwoFactorForm } from './two-factor-form.styles';
import type { TwoFactorFormValues, TwoFactorResponse } from './two-factor-form.types';
export const TwoFactorForm = () => {
  const navigate = useNavigate();
  const [performTwoFactor, {
    isLoading: isOtpLoading
  }] = useTwoFactorMutation();
  const [verifyRecoveryCode, {
    isLoading: isRecoveryLoading
  }] = useVerifyRecoveryCodeMutation();
  const [twoFactorError, setTwoFactorError] = useState<string | null>(null);
  const isAdminLoginRedirectEnabled = useFeatureIsOn('b2b-admin-login-redirect');
  const isLoading = isOtpLoading || isRecoveryLoading;
  const referrer = getLoginReferrer();
  const {
    register,
    handleSubmit,
    formState: {
      errors
    },
    reset
  } = useForm<TwoFactorFormValues>();
  const {
    authenticationCode: authenticationCodeError
  } = errors;
  const navigateToDashboard = (result: TwoFactorResponse | undefined, to: string, condition: boolean) => {
    if (isAdminLoginRedirectEnabled && result?.isB2BAdmin && condition) {
      const companyId = result.companyId;
      navigate(`/client/${companyId}/dashboard`);
    } else {
      navigate(to);
    }
  };
  const handleOnSuccess = (data: TwoFactorFormValues) => {
    setTwoFactorError(null);
    const code = data.authenticationCode;
    // Detection: 6 digits = OTP, 10 characters = recovery code
    const isOTP = code.length === 6 && /^\d{6}$/.test(code);
    const apiCall = isOTP ? performTwoFactor(code) : verifyRecoveryCode(code);
    apiCall.unwrap().then(result => {
      window.sessionStorage.removeItem(LOGIN_REFERRER);
      if (result && result.status === 'error') {
        setTwoFactorError(result.message ?? 'An error has occurred, please try again');
        reset();
      } else if (result && !result.data?.verified) {
        // Check if recovery code was already used
        if (!isOTP && result.data?.codeAlreadyUsed) {
          setTwoFactorError('This recovery code was already used. Please use another recovery code.');
        } else {
          setTwoFactorError('Uh-oh! That authentication code is not valid.');
        }
        reset();
      } else {
        apiSlice.util.resetApiState();
        if (referrer) {
          navigateToDashboard(result.data, referrer, referrer === '/');
        } else {
          navigateToDashboard(result.data, '/dashboard', true);
        }
      }
    }).catch((error: unknown) => {
      const errorMessage = isQueryErrorWithMessage(error) ? error.data.message : 'An error has occurred, please try again';
      setTwoFactorError(errorMessage);
      reset();
    });
  };
  return <StyledTwoFactorForm data-sentry-element="StyledTwoFactorForm" data-sentry-component="TwoFactorForm" data-sentry-source-file="two-factor-form.tsx">
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <StyledForm onSubmit={handleSubmit(handleOnSuccess)} data-sentry-element="StyledForm" data-sentry-source-file="two-factor-form.tsx">
        <header>
          <StyledFormTitle data-sentry-element="StyledFormTitle" data-sentry-source-file="two-factor-form.tsx">Welcome back!</StyledFormTitle>
          <StyledFormSubtitle data-sentry-element="StyledFormSubtitle" data-sentry-source-file="two-factor-form.tsx">Log in to your account.</StyledFormSubtitle>
        </header>
        <p>Please enter your two-factor authentication code. You can find this in your authentication app.</p>
        {twoFactorError && <AlertBanner status={AlertBannerStatus.ERROR} message={twoFactorError} />}
        <StyledControllerWrapper data-sentry-element="StyledControllerWrapper" data-sentry-source-file="two-factor-form.tsx">
          <TextField {...register('authenticationCode', {
          required: AUTHENTICATION_CODE_REQUIRED_MESSAGE,
          validate: value => {
            if (!value) return AUTHENTICATION_CODE_REQUIRED_MESSAGE;
            // Allow either 6-digit OTP or 10-character recovery code
            if (/^\d{6}$/.test(value) || value.length === 10) {
              return true;
            }
            return true;
          }
        })} autoComplete="one-time-code" autoFocus errorMessage={authenticationCodeError ? authenticationCodeError?.message : ''} id="authentication-code" isError={!!authenticationCodeError} label="Authentication Code" placeholder="..." type="text" variant="input" data-sentry-element="TextField" data-sentry-source-file="two-factor-form.tsx" />
        </StyledControllerWrapper>
        <StyledFlex flexDirection="column" gap="1rem" data-sentry-element="StyledFlex" data-sentry-source-file="two-factor-form.tsx">
          <StyledFullWidthButton type="submit" isLoading={isLoading} isDisabled={isLoading} data-sentry-element="StyledFullWidthButton" data-sentry-source-file="two-factor-form.tsx">
            Submit
          </StyledFullWidthButton>
          <p>
            Authentication code doesn&apos;t work? Use your recovery code to access your account or{' '}
            <a href="mailto:support@tryhackme.com">contact us</a> for more information.
          </p>
        </StyledFlex>
      </StyledForm>
    </StyledTwoFactorForm>;
};